import { Box, Tooltip } from "@mui/material";
import { CampaignEmailStatus } from "schemas/dashboard";
import {
  DARK_BROWN,
  HEART_COLOR,
  LIGHT_GREY,
  VARIABLE_TAG_BACKGROUND,
} from "styles/oneOffColor";

interface Props {
  status: CampaignEmailStatus;
  followUpNumber?: number | null;
}

interface ChipConfig {
  label: string;
  color: string;
  bg: string;
  icon: string;
}

const statusMap: Record<CampaignEmailStatus, ChipConfig> = {
  // Purple — pulled from theme.palette.purple (light/dark)
  [CampaignEmailStatus.scheduled]: {
    label: "Scheduled",
    color: "#8084FF",
    bg: "#f2f3fe",
    icon: "fa-regular fa-clock",
  },
  // Grey — awaiting scheduling
  [CampaignEmailStatus.pending]: {
    label: "Pending",
    color: "#6b7280",
    bg: LIGHT_GREY,
    icon: "fa-regular fa-hourglass-half",
  },
  // Grey — on hold while the campaign is paused
  [CampaignEmailStatus.paused]: {
    label: "Paused",
    color: "#6b7280",
    bg: LIGHT_GREY,
    icon: "fa-regular fa-circle-pause",
  },
  // Orange — the app's mail-merge / variable-tag colors
  [CampaignEmailStatus.replied]: {
    label: "Replied",
    color: DARK_BROWN,
    bg: VARIABLE_TAG_BACKGROUND,
    icon: "fa-regular fa-message",
  },
  // Green — theme.palette.primary.light + green.main
  [CampaignEmailStatus.outreach_sent]: {
    label: "Outreach sent",
    color: "#165C4E",
    bg: "#DBEFDD",
    icon: "fa-regular fa-paper-plane",
  },
  // Beige — theme.palette.secondary (light/dark)
  [CampaignEmailStatus.followup_sent]: {
    label: "Follow-up sent",
    color: "#776B5D",
    bg: "#F3EEEA",
    icon: "fa-solid fa-reply",
  },
  // Red — the app's HEART_COLOR
  [CampaignEmailStatus.failed]: {
    label: "Failed",
    color: HEART_COLOR,
    bg: "rgba(192,57,43,0.10)",
    icon: "fa-regular fa-circle-exclamation",
  },
  // Grey
  [CampaignEmailStatus.no_contact]: {
    label: "No contact",
    color: "#6b7280",
    bg: LIGHT_GREY,
    icon: "fa-solid fa-user-slash",
  },
  // Amber — user cancelled the scheduled sends before any went out
  [CampaignEmailStatus.cancelled]: {
    label: "Cancelled",
    color: "#B45309",
    bg: "#FEF3C7",
    icon: "fa-regular fa-calendar-xmark",
  },
};

const tooltipByStatus: Partial<Record<CampaignEmailStatus, string>> = {
  [CampaignEmailStatus.no_contact]:
    "We couldn't find a valid contact for this brand, so nothing was sent.",
  [CampaignEmailStatus.failed]:
    "The contact originally selected is no longer valid — click Retry to try to find a new one.",
  [CampaignEmailStatus.pending]: "This brand is still being scheduled.",
  [CampaignEmailStatus.paused]:
    "This brand will be rescheduled once the campaign is resumed.",
  [CampaignEmailStatus.cancelled]:
    "The scheduled emails were deleted before any were sent — click Retry to reschedule.",
};

export default function CampaignEmailStatusChip({
  status,
  followUpNumber,
}: Props) {
  const config =
    statusMap[status] ?? statusMap[CampaignEmailStatus.outreach_sent];

  const label =
    status === CampaignEmailStatus.followup_sent && followUpNumber
      ? `Follow-up ${followUpNumber} sent`
      : config.label;

  const tooltip = tooltipByStatus[status];

  const chip = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.25,
        py: 0.5,
        borderRadius: 10,
        backgroundColor: config.bg,
        color: config.color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        lineHeight: 1.4,
      }}
    >
      <Box component="i" className={config.icon} sx={{ fontSize: 10 }} />
      {label}
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}
